import { IPublicClientApplication } from '@azure/msal-browser';
import {
  AddressTypeCode,
  EventAttendee,
  Questionnaire,
  QuestionnaireProgress,
  QuestionnaireQuestionRef,
  WinnerAddress,
  WinnerContact,
} from '../../models';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { withApiAccessToken } from '../with-api-access-token';
import {
  createEventAttendee,
  createQuestionnaire,
  deleteEventAttendee,
  deleteUploadedMedia,
  getQuestionnaire,
  getQuestionnaireProgress,
  getQuestionnaireQuestions,
  restoreLogo,
  restoreMedia,
  submitQuestionnaire,
  updateEventAttendee,
  updateQuestionnaire,
  updateQuestionnaireProgress,
  updateWinnerAddress,
  updateWinnerContact,
  uploadLogo,
  uploadMedia,
} from './index';

export const createQuestionnairesApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  createEventAttendee: (instance: IPublicClientApplication, questionnaireId: number, eventAttendee: EventAttendee) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createEventAttendee(fetchContext, questionnaireId, eventAttendee, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createEventAttendee', isInFlight);
    },
  ),
  createQuestionnaire: (instance: IPublicClientApplication, winnerId: number, questionnaire: Questionnaire) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createQuestionnaire(fetchContext, winnerId, questionnaire, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createQuestionnaire', isInFlight);
    },
  ),
  deleteEventAttendee: (instance: IPublicClientApplication, eventAttendeeId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteEventAttendee(fetchContext, eventAttendeeId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteEventAttendee', isInFlight);
    },
  ),
  deleteUploadedMedia: (instance: IPublicClientApplication, uploadedMediaId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteUploadedMedia(fetchContext, uploadedMediaId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteUploadedMedia', isInFlight);
    },
  ),
  getQuestionnaireProgress: (instance: IPublicClientApplication, questionnaireId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getQuestionnaireProgress(fetchContext, questionnaireId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getQuestionnaireProgress', isInFlight);
    },
  ),
  getQuestionnaireQuestions: (instance: IPublicClientApplication, winnerId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getQuestionnaireQuestions(fetchContext, winnerId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getQuestionnaireQuestions', isInFlight);
    },
  ),
  getQuestionnaire: (instance: IPublicClientApplication, questionnaireId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getQuestionnaire(fetchContext, questionnaireId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getQuestionnaire', isInFlight);
    },
  ),
  restoreLogo: (instance: IPublicClientApplication, questionnaireId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => restoreLogo(fetchContext, questionnaireId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('restoreLogo', isInFlight);
    },
  ),
  restoreMedia: (instance: IPublicClientApplication, questionnaireId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => restoreMedia(fetchContext, questionnaireId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('restoreMedia', isInFlight);
    },
  ),
  submitQuestionnaire: (instance: IPublicClientApplication, questionnaireId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => submitQuestionnaire(fetchContext, questionnaireId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('submitQuestionnaire', isInFlight);
    },
  ),
  updateEventAttendee: (instance: IPublicClientApplication, eventAttendeeId: number, eventAttendee: EventAttendee) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateEventAttendee(fetchContext, eventAttendeeId, eventAttendee, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateEventAttendee', isInFlight);
    },
  ),
  updateQuestionnaireProgress: (instance: IPublicClientApplication, questionnaireId: number, questionnaireProgress: QuestionnaireProgress) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateQuestionnaireProgress(fetchContext, questionnaireId, questionnaireProgress, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateQuestionnaireProgress', isInFlight);
    },
  ),
  updateQuestionnaire: (instance: IPublicClientApplication, questionnaireId: number, questionnaireQuestionRef: QuestionnaireQuestionRef) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateQuestionnaire(fetchContext, questionnaireId, questionnaireQuestionRef, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateQuestionnaire', isInFlight);
    },
  ),
  updateWinnerAddress: (instance: IPublicClientApplication, questionnaireId: number, addressTypeCode: AddressTypeCode, winnerAddress: WinnerAddress) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateWinnerAddress(fetchContext, questionnaireId, addressTypeCode, winnerAddress, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateWinnerAddress', isInFlight);
    },
  ),
  updateWinnerContact: (instance: IPublicClientApplication, questionnaireId: number, winnerContact: WinnerContact) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateWinnerContact(fetchContext, questionnaireId, winnerContact, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateWinnerContact', isInFlight);
    },
  ),
  uploadLogo: (instance: IPublicClientApplication, questionnaireId: number, mediaContent: File) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => uploadLogo(fetchContext, questionnaireId, mediaContent, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('uploadLogo', isInFlight);
    },
  ),
  uploadMedia: (instance: IPublicClientApplication, questionnaireId: number, mediaContent: File) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => uploadMedia(fetchContext, questionnaireId, mediaContent, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('uploadMedia', isInFlight);
    },
  ),
});
